import {
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { DateRangeFilter } from '@/components/ui/date-range-filter';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { INCOME_BASE } from '../api';
import { INCOME_SOURCES, incomeSourceLabel } from '../constants';
import { useIncome } from '../queries';
import { useIncomeUi, type LedgerViewMode } from '../store/viewPrefs';
import type { IncomeEntry } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type IncomeOrderingColumn = 'date' | 'description' | 'source' | 'base_amount';

const VIEW_MODES: readonly ViewModeOption<LedgerViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by source', icon: Layers },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

export function IncomeListPage() {
  const view = useIncomeUi((s) => s.view);
  const setView = useIncomeUi((s) => s.setView);
  const ordering = useIncomeUi((s) => s.ordering);
  const setOrdering = useIncomeUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useIncome({
    page,
    search: search || undefined,
    after: from || undefined,
    before: to || undefined,
    ordering,
  });

  const results = data?.results ?? [];
  // Per-currency totals for the rows on this page — kept apart (no FX), so each
  // currency reads as its own subtotal rather than a converted lump sum.
  const pageTotals = results.reduce((acc, e) => {
    const code = e.currency ?? 'USD';
    acc.set(code, (acc.get(code) ?? 0) + Number(e.amount ?? 0));
    return acc;
  }, new Map<string, number>());
  const pageTotalLabel = Array.from(pageTotals.entries())
    .map(([code, amt]) => formatMoney(String(amt), code, { narrowSymbol: true }))
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Income</h1>
        <p className="text-muted-foreground text-sm">
          Read-only ledger — the single source of financial truth (totals roll up to USD base).
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by description…"
              className="pl-8"
              aria-label="Search income"
            />
          </div>
          <DateRangeFilter
            label="Date"
            idPrefix="income-date"
            from={from}
            to={to}
            onFromChange={(v) => { setPage(1); setFrom(v); }}
            onToChange={(v) => { setPage(1); setTo(v); }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `income.csv` without leaving the SPA.
              void exportCsv(INCOME_BASE, {
                search: search || undefined,
                after: from || undefined,
                before: to || undefined,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export income as CSV"
            title="Export income as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <IncomeSkeleton />
      ) : view === 'table' ? (
        <IncomeTable entries={results} ordering={ordering} onOrderingChange={setOrdering} />
      ) : view === 'list' ? (
        <IncomeList entries={results} />
      ) : view === 'cards' ? (
        <IncomeCards entries={results} />
      ) : (
        <IncomeGroupedBySource entries={results} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'entry' : 'entries'} ·{' '}
            <span className="text-foreground font-medium tabular-nums">
              Page total {pageTotalLabel}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface IncomeViewProps {
  entries: IncomeEntry[];
}

interface IncomeTableProps extends IncomeViewProps {
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function IncomeTable({ entries, ordering, onOrderingChange }: IncomeTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<IncomeOrderingColumn>
              column="date"
              value={ordering}
              onChange={onOrderingChange}
            >
              Date
            </SortableTableHead>
            <SortableTableHead<IncomeOrderingColumn>
              column="description"
              value={ordering}
              onChange={onOrderingChange}
            >
              Description
            </SortableTableHead>
            <SortableTableHead<IncomeOrderingColumn>
              column="source"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Source
            </SortableTableHead>
            <TableHead className="text-right">Amount</TableHead>
            <SortableTableHead<IncomeOrderingColumn>
              column="base_amount"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden text-right md:table-cell"
            >
              Base (USD)
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No income entries.
              </TableCell>
            </TableRow>
          ) : (
            entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{formatDate(e.date)}</TableCell>
                <TableCell className="font-medium">{e.description}</TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {incomeSourceLabel(e.source)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMoney(e.amount, e.currency) ?? '—'}
                </TableCell>
                <TableCell className="hidden text-right tabular-nums md:table-cell">
                  {formatMoney(e.base_amount, 'USD') ?? '—'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function IncomeCards({ entries }: IncomeViewProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map((e) => (
        <Card key={e.id}>
          <CardHeader>
            <CardTitle className="text-base">{e.description}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="outline">{incomeSourceLabel(e.source)}</Badge>
            <span className="text-muted-foreground">{formatDate(e.date)}</span>
            <span className="ml-auto font-medium tabular-nums">
              {formatMoney(e.amount, e.currency) ?? '—'}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function IncomeList({ entries }: IncomeViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {entries.map((e) => (
        <li
          key={e.id}
          className="flex items-center gap-3 px-3 py-2 text-sm"
        >
          <span className="min-w-0 flex-1 truncate font-semibold">{e.description}</span>
          <Badge variant="outline" className="hidden shrink-0 sm:inline-flex">
            {incomeSourceLabel(e.source)}
          </Badge>
          <span className="text-muted-foreground text-xs">{formatDate(e.date)}</span>
          <span className="font-medium tabular-nums">
            {formatMoney(e.amount, e.currency) ?? '—'}
          </span>
        </li>
      ))}
    </ul>
  );
}

function IncomeGroupedBySource({ entries }: IncomeViewProps) {
  // Bucket by source, preserving the declared source order; skip empty groups.
  const groups = useMemo(() => {
    return INCOME_SOURCES.map((s) => ({
      key: s.value,
      label: s.label,
      bucket: entries.filter((e) => e.source === s.value),
    }));
  }, [entries]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`income-source-${key}`}>
            <h2
              id={`income-source-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <IncomeCards entries={bucket} />
          </section>
        );
      })}
    </div>
  );
}

function IncomeSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
